import { useEffect, useRef, useState } from 'react';
import api from '../api';
import { notify } from '../lib/notifications';
import { log } from '../lib/logger';

const POLL_INTERVAL_MS = 8000;

// Only show an error to the user after this many CONSECUTIVE failures.
// One transient network blip (phone wake, bank-app return, captive portal) should not
// flip the page to an error state.
const FAILURE_THRESHOLD = 3;

const maybeAnnounce = (previousStatus, order) => {
  const flippedToPaid = previousStatus === 'pending' && (order.status === 'paid' || order.status === 'shipped');
  if (!flippedToPaid) return;
  notify(
    '✅ Payment confirmed',
    `Order ${order.reference} — A$${order.total.toFixed(2)} received.`,
    { tag: `order-paid-${order.id}`, requireInteraction: false },
  );
};

/**
 * Poll an order by reference every 8s while mounted.
 *
 * Resilience rules:
 *  - A 404 immediately surfaces "Order not found" (we know the order is gone).
 *  - A network error / 5xx / timeout is retried silently and only surfaced
 *    after FAILURE_THRESHOLD consecutive failures.
 *  - Any successful fetch resets the failure counter AND clears any prior error,
 *    so the page recovers automatically when the user returns from their bank app.
 *  - When the tab regains focus we trigger an immediate refetch so the user
 *    doesn't have to wait for the next 8s tick.
 */
export const useOrderPolling = (reference) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const mountedRef = useRef(true);
  const lastStatusRef = useRef(null);
  const failureCountRef = useRef(0);
  const pollIdRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;

    const fetchOnce = async () => {
      try {
        const { data } = await api.get(`/orders/by-ref/${reference}`);
        if (!mountedRef.current) return;
        failureCountRef.current = 0;
        maybeAnnounce(lastStatusRef.current, data);
        lastStatusRef.current = data.status;
        setOrder(data);
        setError('');
        setLoading(false);
      } catch (e) {
        if (!mountedRef.current) return;
        const status = e.response?.status;
        if (status === 404) {
          setError('Order not found');
          setLoading(false);
          return;
        }
        failureCountRef.current += 1;
        log.warn(`Order poll failed (${failureCountRef.current}/${FAILURE_THRESHOLD})`, e?.message || e);
        if (failureCountRef.current >= FAILURE_THRESHOLD) {
          setError('Could not load order');
          setLoading(false);
        }
        // Otherwise keep loading state if we never succeeded, or keep showing
        // last known order data if we have it.
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchOnce();
    };

    fetchOnce();
    pollIdRef.current = setInterval(fetchOnce, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      mountedRef.current = false;
      clearInterval(pollIdRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [reference]);

  return { order, loading, error };
};
